import React, { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
  Input,
  InputGroupAddon,
  InputGroup,
  UncontrolledButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  ModalFooter,
  Label,
  FormGroup,
} from 'reactstrap';
import { useApi, IApi, ICurrentDeviceConfig, useDeviceConfig } from 'utils/API';
import { useTranslations, TTranslate } from 'utils/language';
import { useNotifications, INotificationContext, addNotification } from 'utils/NotificationManager';
import BufferedExecutor from 'component_utils/bufferedExecutor';
import BarcodeScanner from 'components/barcode_scanner';
import { HooksHoc, MaybePromise, useConstantRef, useRefInitOnce } from 'component_utils/utils';
import { useHistory, useLocation } from 'react-router';
import VirtualKeyboard, { VirtualKeyboardRef } from './VirtualKeyboard';
import { ColorTypes } from 'styling/stylingVars';
import './style.scss';
import classNames from 'classnames';
import AutoComplete, { AutoCompleteFetcher, AutoCompleteRef } from 'components/Autocomplete';
import { addPrompt, confirm } from 'utils/Prompts';
import Translation from 'utils/Language/Translation';
import { getOneTimeInputFromPeripheral } from 'utils/PeripheralManager';
import { debounce, fromPairs } from 'lodash';
import { FaBarcode } from 'react-icons/fa';
import { openWorkTimeModal, triggerWorkTimeRegistration } from 'routes/production/WorkConfirmation';
import If from 'components/If';
import { useSpecializations } from 'utils/SpecializationProvider';

export type ScannerInputModes = 'none' | 'text' | 'numeric' | 'date';

export type ScannerDropdownItem = {
  color: ColorTypes;
  onClick: any;
  content: ReactNode;
  id: string;
  disabled?: boolean;
};

interface Props {
  api: IApi;
  T: TTranslate;
  notifications: INotificationContext;
  deviceConfig: ICurrentDeviceConfig;
  history: ReturnType<typeof useHistory>;

  isLoading?: boolean;
  inputStep?: any;
  inputType?: ScannerInputModes;
  placeholder: string;
  disabled?: boolean;
  onEnter?: (s: string, ...args: any[]) => MaybePromise<void>;
  customButtons?: ReactNode;
  customDropDowns?: ScannerDropdownItem[];
  commands?: {
    pattern: RegExp;
    action: (args: RegExpMatchArray) => MaybePromise<any>;
  }[];
  priority?: number;
  expectDeviceIdentification?: boolean;

  fetchAutocompleteOptions?: AutoCompleteFetcher;
}

let currentFocusRequest: NodeJS.Timeout = null
export const handleRequestFocus = (event: any) => {
  const anyParentHasAttribute = (el: HTMLElement, attr: string): boolean => {
    if (el.getAttribute(attr) || el.classList.contains(attr)) return true;
    return (el.parentNode &&
      (el.parentNode as HTMLElement).getAttribute &&
      anyParentHasAttribute(el.parentNode as HTMLElement, attr)) as boolean;
  };

  const anyParentIsHidden = (el: HTMLElement): boolean => {
    if (getComputedStyle(el, null).display === 'none') return true
    return el.parentElement && anyParentIsHidden(el.parentElement)
  };

  const findInput = () => {
    let layers = Array.from(document.querySelectorAll(".app,.modal,.layer")).reverse()
    if (layers.length === 0) return;

    // find the input in this layer
    layers = [layers[0]]
    for (const layer of layers) {
      const scanners = Array.from(layer.querySelectorAll(".scannerInput"))
      for (const scanner of scanners) {
        if (anyParentIsHidden(scanner as HTMLElement)) {
          continue
        }
        return scanner as HTMLInputElement
      }
    }  
  }

  // find desired input element
  const input = findInput()
  if (!input) {
    // if no applicable input could be found then no input should currently be focused at all
    currentFocusRequest && clearTimeout(currentFocusRequest);
    currentFocusRequest = null;
    if (document.activeElement?.classList?.contains("scannerInput")) {
      (document.activeElement as HTMLInputElement)?.blur?.()
    }
    return
  }

  // if this focus request was triggered by an event make sure we should apply focus to a scanner
  if (event) {
    // do not hijack focus from these elements
    if (['INPUT', 'SELECT', 'TEXTAREA'].includes(event.target.tagName.toUpperCase())) {
      return;
    }

    // do not hijack focus from label elements
    if (event.target.closest("label")?.htmlFor) {
      return;
    }


    // verify whether or not the clicked element is in a prevent focus section
    const preventAutoFocus = anyParentHasAttribute(event.target, 'data-prevent-auto-focus')

    // check whether we have a force-auto-focus on the element
    const targetHasForceAutoFocus = event.target.getAttribute('data-force-auto-focus')
    const parentButtonHasAutoFocus = event.target.closest("button")?.getAttribute('data-force-auto-focus')
    const hasForceAutoFocus = targetHasForceAutoFocus || parentButtonHasAutoFocus

    // do not autofocus if clicked inside of a prevent autofocus section
    // unless force auto focus is set
    if (!hasForceAutoFocus && preventAutoFocus) {
      return;
    }
  } else {
    // do not hijack focus if the focus is currently given to an input element type
    if (['INPUT', 'SELECT', 'TEXTAREA'].includes(document.activeElement?.tagName?.toUpperCase?.())) {
      return
    }
  }

  currentFocusRequest && clearTimeout(currentFocusRequest);
  currentFocusRequest = setTimeout(() => {
    if (input !== document.activeElement)
      input?.focus?.({ preventScroll: true });
  }, 1);
};

export const initializeScannerFocusHandler = () => {
  const debouncedHandleRequestFocus = debounce(() => handleRequestFocus(null), 250)
  document.addEventListener('click', handleRequestFocus, false);
  window.addEventListener('resize', debouncedHandleRequestFocus);
  return () => {
    document.removeEventListener('click', handleRequestFocus);
    window.removeEventListener('resize', debouncedHandleRequestFocus);
  };
}

export const ScannerFocusTrigger = () => {
  useEffect(() => {
    setTimeout(() => handleRequestFocus(null), 1);
    return () => {
      setTimeout(() => handleRequestFocus(null), 1);
    }
  }, [])
  return <React.Fragment/>
}

const promptForMutliple = (text: ReactNode, fields: { name: string, def: string, label: ReactNode, options: string[] }[], allowCancel: boolean = true): Promise<string> =>
  addPrompt(({ index, id, resolve, afterClosed }) => {
    const [state, setState] = useState(fromPairs(fields.map(it => [it.name, it.def])));
    const [isOpen, setIsOpen] = useState(true);

    const cancel = useCallback(() => {
      resolve(null);
      setIsOpen(false);
    }, [resolve]);

    const ok = useCallback(() => {
      resolve(state);
      setIsOpen(false);
    }, [resolve, state]);

    return (
      <Modal
        onClick={(e) => {
          e.stopPropagation();
        }}
        isOpen={isOpen}
        onOpened={() => {
          if (fields.length === 0) {
            document.getElementById(`prompts-${id}-btn-ok`).focus()
          } else {
            document.getElementById(`prompts-${id}-input-0`).focus()
          }
        }}
        onClosed={afterClosed}
      >
        <ScannerFocusTrigger/>
        <ModalHeader>
          <Translation name="T.prompts.prompt" />
        </ModalHeader>

        <ModalBody>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            {text}
            <br />

            {fields.map((it, idx) => {
              return (
                <React.Fragment key={idx}>
                  <br/>
                  <FormGroup>
                    <Label>
                      {it.label}
                    </Label>
                    <Input 
                      id={`prompts-${id}-input-${idx}`} 
                      type={it.options.length > 0 ? 'select' : 'text'}
                      value={state[it.name]} 
                      onChange={(e) => {
                        const v = e.target.value
                        setState(old => ({ ...old, [it.name]: v}))
                      }}
                      onKeyUp={(e) => {
                        if (e.key === 'Enter' && !e.repeat) {
                          if (idx === fields.length - 1) {
                            e.preventDefault();
                            document.getElementById(`prompts-${id}-btn-ok`).click();
                          } else {
                            document.getElementById(`prompts-${id}-input-${idx+1}`).focus()
                          }
                        }
                      }}
                    >
                      {it.options.length > 0 && <option value={''}></option>}
                      {it.options.map((it, idx) => <option key={idx} value={it}>{it}</option>)}
                    </Input>
                  </FormGroup>
                </React.Fragment>
              )
            })}
          </Form>
        </ModalBody>

        <ModalFooter>
          <If cond={allowCancel}>
            <Button
              id={`prompts-${id}-btn-cancel`}
              className="w-25"
              color="secondary"
              onClick={cancel}
            >
              <Translation name="T.misc.cancel" />
            </Button>
          </If>
          <Button
            id={`prompts-${id}-btn-ok`}
            autoFocus
            className="w-25"
            color="primary"
            onClick={ok}
          >
            <Translation name="T.misc.ok" />
          </Button>
        </ModalFooter>
      </Modal>
    );
  });

const Scanner: FC<Props> = (_props) => {
  const api = useApi();
  const deviceConfig = useDeviceConfig()
  const props = useConstantRef(_props);
  const location = useLocation();
  const executorRef = useRefInitOnce(() => new BufferedExecutor());
  const inputRef = useRef<HTMLInputElement>();
  const keyboardRef = useRef<VirtualKeyboardRef>();
  const autocompleteRef = useRef<AutoCompleteRef>();
  const specialization = useSpecializations()

  const [dropdownIsRunning, setDropdownIsRunning] = useState<{ [k: string]: boolean }>({});

  useEffect(() => {
    // attempt to gain focus
    handleRequestFocus(null)
    return () => {
      handleRequestFocus(null)
    }
  }, [inputRef]);

  const processValue = () => {
    const inputValue = inputRef.current.value;

    // first test whether it is a repeat pattern
    executorRef.current.execute(async () => {
      if (inputValue.startsWith('_be_redirect_')) {
        props.current.history.push(inputValue.substring('_be_redirect_'.length));
        return;
      }

      if (inputValue.startsWith('_be_device_identification_') && !props.current.expectDeviceIdentification) {
        const deviceName = inputValue.substring('_be_device_identification_'.length)
        if (await confirm(<Translation name="T.warnings.session.areYouSureYouWantToContinueOnDevice" params={{ deviceName: <b>{deviceName}</b> }}/>)) {
          await api.post(`/api/v1/auth/transfer_token/create/${deviceName}`, {
            path: location.pathname
          })  
        }
        return;
      }

      if (inputValue.startsWith('_be_override_device_settings_')) {
        const settingsString = inputValue.substring('_be_override_device_settings_'.length)
        const settings = JSON.parse(settingsString)
        deviceConfig.saveDeviceConfiguration(old => {
          return Object.assign({}, old, settings.settings)
        })
        addNotification('success', <Translation name="T.misc.successfullyChangedDeviceSettings"/>, settings.notification || undefined)
        return;
      }

      if (inputValue.startsWith('_be_call_user_api_')) {
        const settingsString = inputValue.substring('_be_call_user_api_'.length)
        const settings = JSON.parse(settingsString)
        
        let fields: any[] = []
        let params: any = {};
        if (settings.promptParamNameTitle && settings.promptParamName) {
          fields.push({
            name: settings.promptParamName,
            def: '',
            label: settings.promptParamNameTitle,
            options: (settings.promptParamOptions || '').split(";").filter((it: string) => it),
          })
        }

        if (settings.promptParamName2Title && settings.promptParamName2) {
          fields.push({
            name: settings.promptParamName2,
            def: '',
            label: settings.promptParamName2Title,
            options: (settings.promptParamOptions2 || '').split(";").filter((it: string) => it),
          })
        }

        if (fields.length > 0) {
          const input = await promptForMutliple(
            settings.confirm,
            fields,
            true
          )
          if (!input) {
            return
          }
          params = input
        } else if (settings.confirm) {
          if (!(await confirm(settings.confirm))) {
            return
          }
        }

        if (settings.method === 'GET') {
          await api.get(settings.url, params)
        } else if (settings.method === 'POST') {
          let body = {}
          if (settings.payload.trim()) {
            body = JSON.parse(settings.payload)
          }
          await api.post(settings.url, body, params)
        }
        return;
      }

      if (inputValue.startsWith('_be_logout_')) {
        // TODO: Wait for all requests to finish
        await api.logout();
        return;
      }

      if (inputValue.startsWith('_be_peripheral_input_')) {
        const config = JSON.parse(inputValue.substring('_be_peripheral_input_'.length))
        const v = await getOneTimeInputFromPeripheral(
          config.usage,
          config.driverName,
          config.deviceName,
          config.settings
        )
        if (props.current.onEnter) {
          await props.current.onEnter(v);
        }
        return;
      }

      if (inputValue.startsWith('_be_open_work_times_')) {
        openWorkTimeModal()
        return;
      }

      if (inputValue.startsWith('_be_worktime_registration_')) {
        const config = JSON.parse(inputValue.substring('_be_worktime_registration_'.length))
        await triggerWorkTimeRegistration(config)
        return;
      }

      // if the onscan function returns true then the barcode has been processed.
      // break of further processing
      if (await specialization?.customActionBarcodes?.onScan?.(inputValue)) {
        return;
      }

      const allCommands = (props.current.commands || []).filter(it => it)
      for (let index = 0; index < allCommands.length; index++) {
        const command = allCommands[index];
        const args = inputValue.match(command.pattern);
        if (args) {
          await command.action(args);
          return;
        }
      }

      if (props.current.onEnter) {
        await props.current.onEnter(inputValue);
      }
    });

    // clear the input
    autocompleteRef.current.clearSearch();
    inputRef.current.value = '';
  };

  return (
    <div className="beScannerInput mb-2">
      <AutoComplete
        ref={autocompleteRef}
        onSelect={(s) => {
          inputRef.current.value = s;
          processValue();
        }}
        fetchAutocompleteOptions={props.current.fetchAutocompleteOptions}
      >
        {({ ref, onBlur, onKeyDownHandler, onKeyUpHandler }) => (
          <>
            <InputGroup>
              <Input
                autoFocus
                innerRef={(r) => {
                  inputRef.current = r;
                  ref.current = r;
                }}
                placeholder={props.current.placeholder}
                type="text"
                inputMode="none"
                className={classNames('scannerInput', props.current.isLoading && 'loading')}
                data-testid="scannerInput"
                priority={props.current.priority || 0}
                disabled={props.current.disabled}
                autoComplete={'off'}
                onKeyDown={onKeyDownHandler}
                onKeyUp={onKeyUpHandler}
                onBlur={onBlur}
              />

              <InputGroupAddon addonType="append">{props.current.customButtons}</InputGroupAddon>

              {props.current.customDropDowns && props.current.customDropDowns.filter((it) => it).length > 0 && (
                <UncontrolledButtonDropdown addonType="append">
                  <DropdownToggle split color="tertiary" style={{ width: '42px' }} />
                  <DropdownMenu>
                    {props.current.customDropDowns
                      .filter((it) => it)
                      .map((dd) => (
                        <DropdownItem
                          key={dd.id}
                          data-testid={dd.id}
                          className={`dropdown-item-${dd.color}`}
                          onClick={async () => {
                            setDropdownIsRunning((old) => ({ ...old, [dd.id]: true }));
                            try {
                              await dd.onClick();
                            } finally {
                              setDropdownIsRunning((old) => ({ ...old, [dd.id]: false }));
                            }
                          }}
                          disabled={dropdownIsRunning[dd.id] || dd.disabled}
                        >
                          {dd.content}
                        </DropdownItem>
                      ))}
                  </DropdownMenu>
                </UncontrolledButtonDropdown>
              )}

              {(props.current.deviceConfig.useEmbeddedBarcodeScanner || props.current.deviceConfig.useVirtualKeyboard) && (
                <InputGroupAddon addonType="append">
                  {props.current.deviceConfig.useEmbeddedBarcodeScanner && (
                    <BarcodeScanner
                      enableOCR={true}
                      shouldAlwaysScan={false}
                      closeOnScan={false}
                      trigger={(disabled, start) => (
                        <Button
                          color="primary"
                          onClick={start}
                          disabled={disabled}
                          data-testid="openCamera"
                        >
                          <FaBarcode/>
                        </Button>
                      )}
                      onScan={(s: string) => {
                        inputRef.current.value = s;
                        processValue();
                      }}
                    />
                  )}
                  {props.current.deviceConfig.useVirtualKeyboard && (
                    <VirtualKeyboard
                      ref={keyboardRef}
                      inputRef={inputRef}
                      inputStep={props.current.inputStep}
                      inputMode={props.current.inputType}
                      onKeyUp={() => {
                        onKeyUpHandler({} as any);
                      }}
                      onEnter={() => {
                        processValue();
                      }}
                    />
                  )}
                </InputGroupAddon>
              )}
            </InputGroup>
          </>
        )}
      </AutoComplete>
    </div>
  );
};

export default HooksHoc(Scanner, () => ({
  api: useApi(),
  T: useTranslations(),
  notifications: useNotifications(),
  history: useHistory(),
  deviceConfig: useDeviceConfig().deviceConfig,
}));
